import { useEffect, useMemo, useRef, useState } from "react";

import {
  compileLoomExplicitSurfaceDeformation,
  deriveLoomExplicitSurfaceHandles,
  LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA,
  type LoomExplicitControlRange,
  type LoomExplicitSurfaceHandle,
  type LoomExplicitSurfaceTarget,
} from "./loomExplicitSurfaceControls";
import type { LoomAuthoringCommand, LoomAuthoringDocument } from "./loomAuthoringModel";
import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";
import type { LoomAuthoringPreviewRequest } from "./useLoomAuthoringHistory";

interface LoomExplicitSurfaceCageProps {
  document: LoomAuthoringDocument;
  nodeId: string;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onPreviewCancel: () => void;
  onPreviewCommand: (command: LoomAuthoringCommand, request: LoomAuthoringPreviewRequest) => string | null;
}

interface CageScalar {
  key: string;
  label: string;
  range: LoomExplicitControlRange;
  step: number;
  unit: string;
  value: number;
}

function precision(step: number): number {
  if (step >= 1) return 0;
  return Math.min(4, Math.max(1, Math.ceil(-Math.log10(step))));
}

function scalars(handle: LoomExplicitSurfaceHandle): CageScalar[] {
  if (handle.kind === "wing-section") {
    return [
      ["leadingEdgeXM", "Leading edge X", "m", 0.001],
      ["centerZM", "Section Z", "m", 0.001],
      ["chordM", "Chord", "m", 0.001],
      ["twistDeg", "Twist", "deg", 0.01],
    ].map(([key, label, unit, step]) => ({
      key: key as string,
      label: label as string,
      range: handle.liveBounds[key as keyof typeof handle.liveBounds],
      step: step as number,
      unit: unit as string,
      value: handle.values[key as keyof typeof handle.values],
    }));
  }
  const keys = handle.kind === "fuselage-point-station"
    ? [["xM", "Station X", "m", 0.001], ["zCenterM", "Center Z", "m", 0.001]] as const
    : [
        ["xM", "Station X", "m", 0.001],
        ["zCenterM", "Center Z", "m", 0.001],
        ["widthM", "Width", "m", 0.001],
        ["heightM", "Height", "m", 0.001],
        ["widthExponent", "Width exponent", "ratio", 0.01],
        ["heightExponent", "Height exponent", "ratio", 0.01],
      ] as const;
  return keys.map(([key, label, unit, step]) => ({
    key,
    label,
    range: handle.liveBounds[key as keyof typeof handle.liveBounds] as LoomExplicitControlRange,
    step,
    unit,
    value: handle.values[key as keyof typeof handle.values] as number,
  }));
}

function target(handle: LoomExplicitSurfaceHandle, key: string, value: number): LoomExplicitSurfaceTarget {
  if (handle.kind === "wing-section") {
    return {
      kind: "wing-section",
      panelId: handle.panelId,
      section: handle.section,
      [key]: value,
    } as LoomExplicitSurfaceTarget;
  }
  return {
    kind: "fuselage-station",
    stationId: handle.stationId,
    [key]: value,
  } as LoomExplicitSurfaceTarget;
}

function handleLabel(handle: LoomExplicitSurfaceHandle): string {
  if (handle.kind === "wing-section") return `${handle.section === "root" ? "Root" : "Tip"} section`;
  return handle.kind === "fuselage-point-station"
    ? `${handle.stationId} point`
    : `${handle.stationId} section`;
}

function WingCageSketch({ handles }: { handles: readonly LoomExplicitSurfaceHandle[] }) {
  const wing = handles.filter((handle): handle is Extract<LoomExplicitSurfaceHandle, { kind: "wing-section" }> => handle.kind === "wing-section");
  if (wing.length !== 2) return null;
  const [root, tip] = wing;
  const values = [root!, tip!].flatMap((handle) => [handle.values.leadingEdgeXM, handle.values.leadingEdgeXM + handle.values.chordM]);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const scale = (value: number) => 16 + ((value - minimum) / Math.max(maximum - minimum, 1e-9)) * 208;
  const path = [
    `${scale(root!.values.leadingEdgeXM)},70`,
    `${scale(tip!.values.leadingEdgeXM)},14`,
    `${scale(tip!.values.leadingEdgeXM + tip!.values.chordM)},14`,
    `${scale(root!.values.leadingEdgeXM + root!.values.chordM)},70`,
  ].join(" ");
  return (
    <svg aria-label="Wing explicit section control cage" className="loom-explicit-cage-sketch" viewBox="0 0 240 84">
      <polygon points={path} />
      <line x1={scale(root!.values.leadingEdgeXM)} x2={scale(root!.values.leadingEdgeXM + root!.values.chordM)} y1="70" y2="70" />
      <line x1={scale(tip!.values.leadingEdgeXM)} x2={scale(tip!.values.leadingEdgeXM + tip!.values.chordM)} y1="14" y2="14" />
      <circle cx={scale(root!.values.leadingEdgeXM)} cy="70" r="4" />
      <circle cx={scale(tip!.values.leadingEdgeXM)} cy="14" r="4" />
      <text x="7" y="77">root</text>
      <text x="7" y="20">tip</text>
    </svg>
  );
}

function CageRange({
  document,
  handle,
  onCommand,
  onPreviewCancel,
  onPreviewCommand,
  scalar,
}: LoomExplicitSurfaceCageProps & { handle: LoomExplicitSurfaceHandle; scalar: CageScalar }) {
  const [draft, setDraft] = useState(scalar.value);
  const draftRef = useRef(scalar.value);
  const dirtyRef = useRef(false);
  useEffect(() => {
    draftRef.current = scalar.value;
    dirtyRef.current = false;
    setDraft(scalar.value);
  }, [scalar.value]);
  useEffect(() => () => onPreviewCancel(), [onPreviewCancel]);

  const plan = (value: number) => compileLoomExplicitSurfaceDeformation(document, {
    schema: LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA,
    commandId: createLoomCommandId(`surface-cage-${handle.handleId}-${scalar.key}`, document.revision),
    expectedRevision: document.revision,
    nodeId: handle.nodeId,
    handles: [target(handle, scalar.key, value)],
  });
  const preview = (value: number) => {
    draftRef.current = value;
    dirtyRef.current = value !== scalar.value;
    setDraft(value);
    if (!dirtyRef.current) {
      onPreviewCancel();
      return;
    }
    const next = plan(value);
    onPreviewCommand(next.command, {
      analyticJacobianParameterId: null,
      backend: "explicit-parametric-regeneration",
      controlId: `${handle.handleId}/${scalar.key}`,
    });
  };
  const cancel = () => {
    dirtyRef.current = false;
    draftRef.current = scalar.value;
    setDraft(scalar.value);
    onPreviewCancel();
  };
  const commit = () => {
    if (!dirtyRef.current) return;
    const value = draftRef.current;
    dirtyRef.current = false;
    onPreviewCancel();
    const error = onCommand(plan(value).command, {
      origin: "gui.control",
      subjectIds: [handle.handleId],
    });
    if (error) cancel();
  };
  const digits = precision(scalar.step);
  return (
    <label className="loom-explicit-cage-control" data-testid="loom-explicit-cage-control" data-surface-coordinate={scalar.key}>
      <span>{scalar.label}<output>{draft.toFixed(digits)} {scalar.unit}</output></span>
      <input
        aria-label={`${handleLabel(handle)} ${scalar.label}`}
        max={scalar.range.maximum}
        min={scalar.range.minimum}
        onBlur={commit}
        onInput={(event) => preview(event.currentTarget.valueAsNumber)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
        onKeyUp={(event) => {
          if (event.key.startsWith("Arrow") || event.key === "Home" || event.key === "End") commit();
        }}
        onPointerCancel={cancel}
        onPointerUp={commit}
        step={scalar.step}
        type="range"
        value={draft}
      />
    </label>
  );
}

export function LoomExplicitSurfaceCage(props: LoomExplicitSurfaceCageProps) {
  const { document, nodeId } = props;
  const result = useMemo(() => {
    try {
      return { handles: deriveLoomExplicitSurfaceHandles(document, nodeId), error: null };
    } catch (error) {
      return { handles: [] as readonly LoomExplicitSurfaceHandle[], error: error instanceof Error ? error.message : String(error) };
    }
  }, [document, nodeId]);
  if (!result.handles.length) return null;
  return (
    <details className="loom-explicit-cage" data-node-id={nodeId} data-testid="loom-explicit-surface-cage">
      <summary><span>Explicit surface control cage</span><em>{result.handles.length} sections</em></summary>
      <div className="loom-explicit-cage-body">
        <WingCageSketch handles={result.handles} />
        <p>Move the parametric section cage, not tessellation vertices. Small moves preview live; the conforming trim and shared seams still require an exact rebuild.</p>
        {result.handles.map((handle) => (
          <section data-handle-id={handle.handleId} key={handle.handleId}>
            <strong>{handleLabel(handle)}</strong>
            {scalars(handle).map((scalar) => <CageRange {...props} handle={handle} key={scalar.key} scalar={scalar} />)}
          </section>
        ))}
      </div>
    </details>
  );
}
